import React, { useState, useEffect } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import './chartwithcontrols.css';

const ChartWithControls = ({ columns = [], data = [], column_types = {}, hasError, error }) => {
    // TODOS LOS HOOKS PRIMERO - antes de cualquier condicional o retorno
    // Estado para los controles del gráfico
    const [xAxis, setXAxis] = useState('');
    const [yAxis, setYAxis] = useState('');
    const [chartType, setChartType] = useState('Bar');
    const [aggregationMethod, setAggregationMethod] = useState('sum');
    const [dataLimit, setDataLimit] = useState(100);

    // Protección contra errores de estructura - asegurarse de que columns exista
    const safeColumns = columns || [];

    // Intentar determinar tipos de columnas si no se proporcionan
    const safeColumnTypes = column_types || {};
    if (Object.keys(safeColumnTypes).length === 0 && data && data.length > 0) {
        // Intentar determinar tipos de columnas basados en los datos
        safeColumns.forEach(col => {
            const sampleValue = data[0][col];
            if (typeof sampleValue === 'number') {
                safeColumnTypes[col] = 'numeric';
            } else {
                safeColumnTypes[col] = 'categorical';
            }
        });
    }

    // Filtrar columnas categóricas y numéricas
    const categoricalColumns = safeColumns.filter(col => safeColumnTypes[col] === 'categorical');
    const numericColumns = safeColumns.filter(col => safeColumnTypes[col] === 'numeric');

    // Inicializar ejes X e Y cuando los datos están disponibles
    useEffect(() => {
        if (categoricalColumns.length > 0 && xAxis === '') {
            setXAxis(categoricalColumns[0]);
        }
        if (numericColumns.length > 0 && yAxis === '') {
            setYAxis(numericColumns[0]);
        }
    }, [categoricalColumns, numericColumns, xAxis, yAxis]);

    // Cambiar los ejes según el tipo de gráfico
    useEffect(() => {
        if (chartType === 'Pie') {
            if (categoricalColumns.length > 0) setXAxis(categoricalColumns[0]);
            if (numericColumns.length > 0) setYAxis(numericColumns[0]);
        }
    }, [chartType, categoricalColumns, numericColumns]);

    // Función de renderizado del componente
    const renderContent = () => {
        // Si hay un error, mostrar mensaje de error en lugar del gráfico
        if (hasError || error) {
            return (
                <div className="error-visualization">
                    <div className="error-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
                            <path d="M12 8L12 13" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 16L12.01 16" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="error-title">No se pudo generar la visualización</h3>
                    <p className="error-message">{error || "Ha ocurrido un error al procesar los datos. Por favor, intenta con otra consulta."}</p>
                </div>
            );
        }

        // Verificar si los datos son válidos
        if (!data || !Array.isArray(data) || data.length === 0) {
            return (
                <div className="error-visualization">
                    <div className="error-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#f0ad4e" strokeWidth="2"/>
                            <path d="M12 8L12 13" stroke="#f0ad4e" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 16L12.01 16" stroke="#f0ad4e" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="error-title">No hay datos disponibles</h3>
                    <p className="error-message">No se encontraron datos para visualizar. Intenta con otra consulta.</p>
                </div>
            );
        }

        // Si no hay ejes seleccionados y no hay columnas disponibles, mostrar mensaje
        if ((categoricalColumns.length === 0 || numericColumns.length === 0) && 
            (data.length > 0 && safeColumns.length > 0)) {
            return (
                <div className="error-visualization">
                    <div className="error-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#5bc0de" strokeWidth="2"/>
                            <path d="M12 8L12 13" stroke="#5bc0de" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 16L12.01 16" stroke="#5bc0de" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="error-title">No se puede generar un gráfico</h3>
                    <p className="error-message">
                        Intenta de nuevo con otra consulta, por favor.
                    </p>
                </div>
            );
        }

        // Verificar que tengamos ejes seleccionados
        if (!xAxis || !yAxis) {
            return (
                <div className="loader-container">
                    <div className="loader"></div>
                    <p>Preparando visualización...</p>
                </div>
            );
        }

        try {
            // Agrupar los datos por el eje X
            const groupDataByXAxis = (data, xAxis, yAxis, method) => {
                const groupedData = data.reduce((acc, row) => {
                    const xValue = String(row[xAxis] || 'N/A');
                    const yValue = parseFloat(row[yAxis]) || 0;
        
                    if (!acc[xValue]) {
                        acc[xValue] = { sum: 0, count: 0 };
                    }
                    acc[xValue].sum += yValue;
                    acc[xValue].count += 1;
                    return acc;
                }, {});
        
                return {
                    labels: Object.keys(groupedData),
                    data: Object.values(groupedData).map(item =>
                        method === 'sum' ? item.sum : item.sum / item.count
                    ),
                };
            };
        
            // Limitar los datos si es necesario
            const limitedData = dataLimit === 'No limit' ? data : data.slice(0, parseInt(dataLimit, 10));
        
            // Agrupar los datos antes de crear el chartData
            const groupedData = groupDataByXAxis(limitedData, xAxis, yAxis, aggregationMethod);
        
            // Formato de los datos para Chart.js
            const chartData = {
                labels: groupedData.labels,
                datasets: [{
                    label: yAxis,
                    data: groupedData.data,
                    backgroundColor: chartType === 'Pie'
                        ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4D5360']
                        : 'rgba(75, 192, 192, 0.6)',
                    borderColor: chartType === 'Line' ? 'rgba(75, 192, 192, 1)' : '',
                    borderWidth: chartType === 'Line' ? 2 : 1,
                }],
            };
        
            // Renderizar el gráfico
            const renderChart = () => {
                const chartHeight = 300;
        
                try {
                    if (chartType === 'Bar') {
                        return <Bar data={chartData} height={chartHeight} options={{ maintainAspectRatio: false }} />;
                    } else if (chartType === 'Line') {
                        return <Line data={chartData} height={chartHeight} options={{ maintainAspectRatio: false }} />;
                    } else if (chartType === 'Pie') {
                        return <Pie data={chartData} height={chartHeight} options={{ maintainAspectRatio: false }} />;
                    }
                } catch (chartError) {
                    console.error('Error rendering chart:', chartError);
                    return (
                        <div className="error-visualization">
                            <h3 className="error-title">Error al renderizar el gráfico</h3>
                            <p className="error-message">No se pudo generar la visualización con los datos seleccionados, intenta otra consulta por favor.</p>
                        </div>
                    );
                }
            };
        
            return (
                <div className="chart-container">
                    <div className="controls-section">
                        {/* Límite de datos */}
                        <div className="control-group">
                            <label htmlFor="data-limit">Data Limit</label>
                            <select id="data-limit" value={dataLimit} onChange={(e) => setDataLimit(e.target.value)}>
                                <option value={100}>100</option>
                                <option value={500}>500</option>
                                <option value={1000}>1000</option>
                                <option value="No limit">No limit</option>
                            </select>
                        </div>
        
                        {/* Tipo de gráfico */}
                        <div className="control-group">
                            <label htmlFor="chart-type">Chart Type</label>
                            <select id="chart-type" value={chartType} onChange={(e) => setChartType(e.target.value)}>
                                <option value="Bar">Bar</option>
                                <option value="Line">Line</option>
                                <option value="Pie">Pie</option>
                            </select>
                        </div>
        
                        {/* Eje X */}
                        <div className="control-group">
                            <label htmlFor="x-axis">X Axis</label>
                            <select id="x-axis" value={xAxis} onChange={(e) => setXAxis(e.target.value)}>
                                {categoricalColumns.map(col => (
                                    <option key={col} value={col}>{col}</option>
                                ))}
                            </select>
                        </div>
        
                        {/* Eje Y */}
                        <div className="control-group">
                            <label htmlFor="y-axis">Y Axis</label>
                            <select id="y-axis" value={yAxis} onChange={(e) => setYAxis(e.target.value)}>
                                {numericColumns.map(col => (
                                    <option key={col} value={col}>{col}</option>
                                ))}
                            </select>
                        </div>
        
                        {/* Método de agregación */}
                        <div className="control-group">
                            <label htmlFor="aggregation-method">Aggregation Method</label>
                            <select id="aggregation-method" value={aggregationMethod} onChange={(e) => setAggregationMethod(e.target.value)}>
                                <option value="sum">Sum</option>
                                <option value="average">Average</option>
                            </select>
                        </div>
                    </div>
        
                    <div className="chart-section">
                        {renderChart()}
                    </div>
                </div>
            );
        } catch (error) {
            console.error('Error general en ChartWithControls:', error);
            return (
                <div className="error-visualization">
                    <div className="error-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
                            <path d="M12 8L12 13" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 16L12.01 16" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <h3 className="error-title">Error inesperado</h3>
                    <p className="error-message">Ha ocurrido un error al procesar los datos. Por favor, intenta con otra consulta.</p>
                </div>
            );
        }
    };

    // Renderizado principal - todos los hooks ya han sido llamados
    return renderContent();
};

export default ChartWithControls;